import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
	Box,
	Container,
	Tabs,
	Tab,
	Card,
	CardContent,
	Button,
	Typography,
	Breadcrumbs,
	Link,
} from '@mui/material';
import { ArrowBack, Save } from '@mui/icons-material';
import { useLazyQuery, useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import { Expense } from '../../gql/graphql';
import Loader from '../../components/generic/Loader';
import { ADD_UPDATE_EXPENSE, GET_EXPENSE_BY_ID } from '../../gql/queries';
import { useShopDetails } from '../../hooks/use-shop';

// Tab Components
import BasicInfoTab from '../../components/expense/BasicInfoTab';
import AnalyticsTab from '../../components/expense/AnalyticsTab';
import AdvancedTab from '../../components/expense/AdvancedTab';

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

function TabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;
	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`expense-tabpanel-${index}`}
			aria-labelledby={`expense-tab-${index}`}
			{...other}
		>
			{value === index && <Box sx={{ py: 3 }}>{children}</Box>}
		</div>
	);
}

function ExpenseDetailPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { shopId, locationId } = useShopDetails();
	const [currentTab, setCurrentTab] = useState(0);
	const [expenseData, setExpenseData] = useState<Partial<Expense>>({});
	const [isLoading, setIsLoading] = useState(false);

	const isEditMode = !!id && id !== 'new';

	const [getExpenseById] = useLazyQuery<Record<'expense', Expense>>(
		GET_EXPENSE_BY_ID
	);

	const [addUpdateExpense, { loading: saving }] = useMutation(ADD_UPDATE_EXPENSE, {
		onCompleted: (data) => {
			toast.success(
				`Expense: ${data.addUpdateExpense.name} ${isEditMode ? 'updated' : 'created'} successfully.`
			);
			if (!isEditMode) {
				navigate(`/dashboard/expenses/${data.addUpdateExpense._id}/edit`);
			}
		},
		onError: (error) => {
			toast.error(error.message);
		},
	});

	useEffect(() => {
		if (isEditMode) {
			loadExpense();
		}
	}, [id]);

	const loadExpense = async () => {
		setIsLoading(true);
		try {
			const { data } = await getExpenseById({
				variables: { id: id! },
			});
			if (data?.expense) {
				setExpenseData(data.expense);
			}
		} catch (error: any) {
			toast.error(error.message);
		} finally {
			setIsLoading(false);
		}
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
		setCurrentTab(newValue);
	};

	const handleSave = async () => {
		try {
			// Validate required fields
			if (!expenseData.name || !expenseData.price) {
				toast.error('Please fill in all required fields (Name, Amount)');
				return;
			}

			// Validate price
			if (expenseData.price && expenseData.price < 1) {
				toast.error('Amount must be greater than 0');
				return;
			}

			await addUpdateExpense({
				variables: { expense: expenseData },
			});
		} catch (error: any) {
			toast.error(error?.message);
		}
	};

	const updateExpenseData = (updates: Partial<Expense>) => {
		setExpenseData((prev) => ({ ...prev, ...updates }));
	};

	return (
		<>
			<Loader isLoading={isLoading || saving} />
			<Box
				component="main"
				sx={{
					flexGrow: 1,
					py: 4,
				}}
			>
				<Container maxWidth="xl">
					{/* Header */}
					<Box sx={{ mb: 3 }}>
						<Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
							<Button
								startIcon={<ArrowBack />}
								onClick={() => navigate('/dashboard/expenses')}
								sx={{ mr: 2 }}
							>
								Back
							</Button>
							<Box sx={{ flexGrow: 1 }}>
								<Typography variant="h4">
									{isEditMode ? 'Edit Expense' : 'Create New Expense'}
								</Typography>
								<Breadcrumbs sx={{ mt: 1 }}>
									<Link
										underline="hover"
										color="inherit"
										onClick={() => navigate('/dashboard/expenses')}
										sx={{ cursor: 'pointer' }}
									>
										Expenses
									</Link>
									<Typography color="text.primary">
										{isEditMode ? expenseData.name || 'Edit' : 'New Expense'}
									</Typography>
								</Breadcrumbs>
							</Box>
							<Button
								variant="contained"
								startIcon={<Save />}
								onClick={handleSave}
								disabled={saving}
							>
								Save Expense
							</Button>
						</Box>
					</Box>

					{/* Tabs */}
					<Card>
						<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
							<Tabs
								value={currentTab}
								onChange={handleTabChange}
								variant="scrollable"
								scrollButtons="auto"
							>
								<Tab label="Basic Information" />
								<Tab label="Analytics" disabled={!isEditMode} />
								<Tab label="Advanced" />
							</Tabs>
						</Box>

						<CardContent>
							<TabPanel value={currentTab} index={0}>
								<BasicInfoTab
									expenseData={expenseData}
									updateExpenseData={updateExpenseData}
								/>
							</TabPanel>

							<TabPanel value={currentTab} index={1}>
								{isEditMode && expenseData._id && (
									<AnalyticsTab expenseId={expenseData._id} />
								)}
							</TabPanel>

							<TabPanel value={currentTab} index={2}>
								<AdvancedTab
									expenseData={expenseData}
									updateExpenseData={updateExpenseData}
								/>
							</TabPanel>
						</CardContent>
					</Card>
				</Container>
			</Box>
		</>
	);
}

export default ExpenseDetailPage;
